import { Tag } from "antd";
import { ColumnType } from "antd/lib/table";
import { ColumnConfig } from "hooks/commons/useColumnSelector";

/**
 * Representa un registro genérico en tablas de resumen o diferencias.
 * Cada fila puede tener valores string | number | boolean | undefined.
 */
export interface SummaryRowData {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Tipo para columnas de Ant Design con datos tipados.
 */
export interface TableColumnType {
  title: string;
  dataIndex: string;
  key: string;
  render?: (
    value: string | number | boolean | undefined,
    record: SummaryRowData
  ) => React.ReactNode;
}

/**
 * Intenta parsear un string como JSON o CSV en un arreglo de objetos.
 * - Si es JSON válido → lo devuelve como array.
 * - Si es CSV válido → lo convierte en array de objetos.
 */
export function parseFileToData(fileContent: string): SummaryRowData[] {
  try {
    // Intentar JSON
    try {
      const jsonData = JSON.parse(fileContent);
      if (Array.isArray(jsonData)) return jsonData;
      return [jsonData];
    } catch {
      // Fallback a CSV
      const lines = fileContent
        .split("\n")
        .filter((line) => line.trim() !== "");
      if (lines.length < 2) return [];
      const headers = lines[0].split(",").map((header) => header.trim());
      return lines.slice(1).map((line) => {
        const values = line.split(",").map((value) => value.trim());
        const row: SummaryRowData = {};
        headers.forEach((header, index) => {
          const val = values[index] || "";
          row[header] = isNaN(Number(val)) ? val : Number(val);
        });
        return row;
      });
    }
  } catch (error) {
    console.error("Error parsing file:", error);
    return [];
  }
}

/**
 * Genera automáticamente columnas de una tabla a partir de los datos.
 * Si un campo está vacío → muestra un Tag rojo "Sin valor".
 */
export function generateColumns(data: SummaryRowData[]): TableColumnType[] {
  if (data.length === 0) return [];
  const sampleRow = data[0];
  return Object.keys(sampleRow).map((key) => ({
    title: key.replace(/_/g, " ").toUpperCase(),
    dataIndex: key,
    key,
    render: (value: string | number | boolean | undefined) => {
      if (typeof value === "number") return value.toLocaleString();
      return value || <Tag color="red">Sin valor</Tag>;
    },
  }));
}

/**
 * Ordena las columnas según el campo `order` y/o alfabéticamente
 */
export function sortColumnsByOrder(columns: ColumnConfig[]): ColumnConfig[] {
  return [...columns].sort((a, b) => {
    // Si ambas tienen order, ordenar por order
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // Si solo a tiene order, a va primero
    if (a.order !== undefined) {
      return -1;
    }
    // Si solo b tiene order, b va después
    if (b.order !== undefined) {
      return 1;
    }
    // Si ninguna tiene order, ordenar alfabéticamente por título
    return a.title.localeCompare(b.title);
  });
}

/**
 * Obtiene el orden de las keys basado en la configuración de columnas
 */
export function getColumnOrder(columns: ColumnConfig[]): string[] {
  const sorted = sortColumnsByOrder(columns);
  return sorted.map((col) => col.key);
}

export function createColumnGroup<T>(
  groupName: string,
  children: ColumnType<T>[]
) {
  return {
    title: groupName,
    children: children,
  };
}
